import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'material-ui';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import { withStyles } from 'material-ui/styles';
import Paper from 'material-ui/Paper';

import * as actions from '../inspection.actions';
import { getInspectionById } from '../inspection.selectors';
import { getInspectionTitle } from '../../inspectionType/inspectionType.selectors';

import { omitEmpty } from '../../../utils';

import { PageAppBar } from '../../core/components/';
import { InspectionFieldTemplate } from '../../common/components/form/templates';
import { SchemaForm, fields, widgets } from '../../common/components/form';
import { InspectionFormContext, InspectionFormSnackBar } from './';
import { uppercaseFirst } from '../../../utils/user-utils';
import { ICONS } from '../../../icons/Icons';
import Icon from '../../../icons/Icon';
import { getCurrentUserRole, getCurrentUserCompany } from '../../user/user.selectors';
import { UserRoles } from '../../user/user.constants';

const styles = theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
  },
  content: {
    maxWidth: 1040,
    margin: theme.spacing.unit + 'px auto',
  },
  appBar: {
    maxWidth: 1040,
    header: {
      paddingRight: '5rem',
      '@media (max-width:520px)': {
        marginRight: '2rem',
      },
    },
  },
  buttonWrap: {
    maxWidth: 1040,
    margin: '0 auto',
    position: 'relative',
    paddingRight: theme.spacing.unit * 2 + 'px',
    '@media (max-width:520px)': {
      display: 'none',
    },
  },
  buttonWrapMobile: {
    display: 'none',
    position: 'absolute',
    top: '3rem',
    right: '0',
    '@media (max-width:520px)': {
      display: 'block',
    },
  },
  buttonCopy: {
    position: 'absolute',
    right: 0,
    bottom: '0.5rem',
    color: '#fff',
  },
  buttonCopyDisabled: {
    position: 'absolute',
    right: 0,
    bottom: '0.5rem',
    color: '#aaa',
  },
});
class InspectionForm extends Component {
  static propTypes = {
    backPath: PropTypes.string.isRequired,
    inspection: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    deleteInspection: PropTypes.func.isRequired,
    readInspection: PropTypes.func.isRequired,
    readInspectionTypes: PropTypes.func.isRequired,
    updateInspection: PropTypes.func.isRequired,
    copyInspection: PropTypes.func.isRequired,
    updateGeneratedInspection: PropTypes.func.isRequired,
    subtitle: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      frontendData: props.inspection && props.inspection.formData,
      errorsLoose: [],
      errorsStrict: [],
    };
  }

  componentDidMount = () => {
    const { readInspection, readInspectionTypes, id } = this.props;
    readInspection(id);
    readInspectionTypes();
  };

  componentWillUnmount() {
    //TODO check how data is saved offline
    const { inspection } = this.props;
    if (inspection.serverId && inspection.status === 'new' && !this.props.isEditMode) {
      const { deleteInspection } = this.props;
      deleteInspection(inspection.serverId);
    }
  }

  componentWillReceiveProps = (nextProps, nextState) => {
    if (
      (!this.state.frontendData ||
        (nextProps.inspection.serverId &&
          (!this.props.inspection.serverId || this.props.inspection.serverId !== nextProps.inspection.serverId))) &&
      nextProps.inspection &&
      nextProps.inspection.formData
    ) {
      this.setState({
        frontendData: nextProps.inspection.formData,
      });
    }
  };

  throttledUpdate = debounce(
    formData => {
      const { updateInspection, updateGeneratedInspection } = this.props;
      //console.log('saving', formData);
      if (this.props.inspection.serverId) {
        updateInspection({ formData });
      } else {
        updateGeneratedInspection({ [formData._id]: { ...formData } });
      }
    },
    500,
    { leading: false }
  );

  onInit = event => {
    this.updateState(event);
  };

  onChange = event => {
    this.updateState(event);

    const dataToSaveWithoutEmptyObjects = omitEmpty(JSON.parse(JSON.stringify(event.formData)));
    const dataToSave = omit(dataToSaveWithoutEmptyObjects, event.errorKeys);

    this.throttledUpdate(dataToSave);
  };

  updateState = event => {
    this.setState({
      frontendData: event.formData,
      errorsLoose: event.errorKeys,
      errorsStrict: event.errorKeysStrict,
    });
  };

  onSubmit = event => {
    event.preventDefault();
  };

  duplicate = () => {
    const { copyInspection, id } = this.props;
    // console.log('duplicate report, id:', id);
    copyInspection(id);
  };

  isAvailable = () => {
    const { company, online } = this.props;
    let formId = this.props.inspection.formSchema.id;
    return( !online || ( online && (company && company === 'wihuri') && (formId && formId.split('.')[0] === 'cat') ) )
  };

  render() {
    const {
      backPath,
      inspection: { formSchema = null, formContext = null, uiSchema = null, validatorSchema = null },
      subtitle = '',
      classes,
      online,
      role,
    } = this.props;

    if (!formSchema || !this.state.frontendData) return null;
    const identifier = this.state.frontendData.identifier || '';
    const title = `ID ${identifier}`;
    const isGuest = role === UserRoles.GUEST;
    return (
      <InspectionFormContext
        errorsLoose={this.state.errorsLoose}
        errorsStrict={this.state.errorsStrict}
        formSchema={formSchema}
        {...formContext}
        render={formContext => (
          <div className={classes.root}>
            <PageAppBar title={title} subtitle={uppercaseFirst(subtitle)} backPath={backPath} />

            {!isGuest && (
              <div className={classes.buttonWrap}>
                <Button disabled={this.isAvailable()} onClick={this.duplicate} className={this.isAvailable() ? classes.buttonCopyDisabled : classes.buttonCopy }>
                  Luo Kopio
                </Button>
              </div>
            )}

            <div className={classes.buttonWrapMobile}>
              <Button onClick={this.duplicate}>
                <Icon fill="#fff" icon={ICONS.COPY} width="24px" height="24px" />
              </Button>
            </div>

            <Paper className={classes.content}>
              <SchemaForm
                noHtml5Validate
                noValidate
                autoComplete="off"
                FieldTemplate={InspectionFieldTemplate}
                showErrorList={false}
                schema={formSchema}
                uiSchema={uiSchema}
                fields={fields}
                widgets={widgets}
                formData={this.state.frontendData}
                formContext={formContext}
                validatorSchema={validatorSchema}
                onBlur={this.onBlur}
                onChange={this.onChange}
                onInit={this.onInit}
                onError={this.onError}
                onSubmit={this.onSubmit}
              >
                <span />
              </SchemaForm>
            </Paper>
            <InspectionFormSnackBar online={online} formContext={formContext} />
          </div>
        )}
      />
    );
  }
}

const mapStateToProps = (state, { match, backPath }) => {
  let { params: { id }, path } = match;
  let inspection = getInspectionById(state, id);
  if (inspection.serverId && inspection.serverId !== id) {
    inspection = getInspectionById(state, inspection.serverId);
  }
  const { formSchema = null } = inspection;
  const subtitle = formSchema && getInspectionTitle(state, formSchema.id);
  const isEditMode = path.indexOf('edit') !== -1;
  const constructBackPathFromId = () => {
    const idSegments = formSchema.id.split('.');
    idSegments.pop();
    return idSegments.join('/');
  };
  backPath = backPath || (formSchema && formSchema.id ? `/inspection/new/${constructBackPathFromId()}` : '/');
  const online = state.offline.online;
  const role = getCurrentUserRole(state);
  const company = getCurrentUserCompany(state);
  return {
    backPath,
    id,
    inspection,
    online,
    subtitle,
    isEditMode,
    role,
    company,
  };
};

export default connect(mapStateToProps, Object.assign({}, actions))(withStyles(styles)(InspectionForm));



// WEBPACK FOOTER //
// ./src/modules/inspection/components/InspectionForm.js